<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useCreativeInsight } from '../composables/useCreativeInsight'

import CreativeSliderCard from './CreativeSliderCard.vue'
import {
  summaryPanelActionsClass,
  summaryPanelClass,
  summaryPanelDetailsClass,
  summaryPanelHeaderClass,
  summaryPanelTabsClass,
  summaryPanelTitleClass,
} from './summaryPanelClasses'

import {
  REPORT_TOOLBAR_ICONS,
  ReportSectionTitle,
  ReportTableCustomizePanel,
  ReportToolbarSelect,
  toReportColumnOption,
  type ReportTableCustomization,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { Alert, Button, Drawer, Icon, type SelectOption } from '@/shared/ui'

const props = defineProps<{ defaultPostData?: string }>()
const emit = defineEmits<{ 'has-data-change': [value: boolean] }>()
const { t } = useI18n()
const drawerOpen = ref(false)
// The slider's arrows live in the header beside the dropdowns (legacy layout); the carousel binds them.
const previousButton = ref<HTMLButtonElement | null>(null)
const nextButton = ref<HTMLButtonElement | null>(null)
const SLIDES_PER_VIEW = 2
const navArrowClass =
  'summary-creative__nav inline-flex size-11 flex-none items-center justify-center p-0 border-0 rounded-md bg-transparent text-primary-muted cursor-pointer transition-colors duration-150 hover:bg-surface-sunken hover:text-primary disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent [&.swiper-button-lock]:hidden'
const insight = useCreativeInsight(() => props.defaultPostData)
const typeOptions = computed<SelectOption<'Image' | 'Video'>[]>(() =>
  insight.types.value.map((value) => ({
    value,
    label: t(`summary.creative.types.${value.toLowerCase()}`),
  })),
)
const metricOptions = computed<SelectOption<string>[]>(() =>
  insight.columns.value
    .filter((column) => column.numeric)
    .map((column) => toReportColumnOption(column)),
)
const requiredFields = computed(() =>
  insight.selectedType.value === 'Video' ? ['thumbnail_url'] : ['image_url'],
)
const visibleFields = computed(() => [
  ...requiredFields.value,
  ...insight.dimensions.value.map((column) => column.field),
  ...insight.metrics.value.map((column) => column.field),
])

// The drawer body is the table customize panel (search, collapsible groups, metric sources), so
// both surfaces present columns the same way; its visible set splits back into the two axes here.
function applyCustomization({ visibleFields: fields }: ReportTableCustomization): void {
  const selected = new Set(fields)
  const columns = insight.columns.value.filter((column) => selected.has(column.field))
  insight.updateSettings({
    dimensions: columns.filter((column) => !column.numeric),
    metrics: columns.filter((column) => column.numeric),
  })
}

const metricField = computed({
  get: () => insight.selectedMetric.value?.field ?? null,
  set: (field: string | null) => {
    insight.selectedMetric.value =
      insight.columns.value.find((column) => column.field === field) ?? null
  },
})

watch(
  () => [insight.types.value.length, insight.rows.value.length, insight.state.value] as const,
  ([typeCount, rowCount, state]) =>
    emit('has-data-change', typeCount > 0 && (rowCount > 0 || state === 'error')),
  { immediate: true },
)
</script>

<template>
  <section :class="[summaryPanelClass, 'relative pb-0']">
    <header :class="summaryPanelHeaderClass">
      <div class="flex min-w-0 items-center gap-md max-lg:flex-wrap">
        <ReportSectionTitle
          :title="t('summary.creative.title')"
          :tabs="insight.tabs.value"
          :title-class="summaryPanelTitleClass"
          :tabs-class="summaryPanelTabsClass"
        />
        <RouterLink
          :class="[summaryPanelDetailsClass, 'summary-panel__details']"
          to="/creative-dashboard"
        >
          <span>{{ t('summary.creative.details') }}</span>
          <Icon name="chevron-right" :size="12" />
        </RouterLink>
      </div>
      <div
        v-if="insight.types.value.length && insight.state.value !== 'error'"
        :class="[summaryPanelActionsClass, 'summary-panel__actions summary-creative__actions']"
      >
        <ReportToolbarSelect
          v-model="insight.selectedType.value"
          class="summary-creative__group"
          :label="t('summary.creative.group')"
          :options="typeOptions"
          icon="sliders"
        />
        <ReportToolbarSelect
          v-model="metricField"
          class="summary-creative__sort"
          :label="t('summary.creative.sortBy')"
          :options="metricOptions"
          :icon="REPORT_TOOLBAR_ICONS.sort"
          searchable
        />
        <Button
          class="summary-creative__customize text-primary-muted"
          variant="ghost"
          size="lg"
          square
          :aria-label="t('summary.actions.customize')"
          :title="t('summary.actions.customize')"
          @click="drawerOpen = true"
        >
          <Icon :name="REPORT_TOOLBAR_ICONS.customize" :size="18" aria-hidden="true" />
        </Button>
        <template v-if="insight.state.value !== 'empty'">
          <button
            v-show="insight.rows.value.length > SLIDES_PER_VIEW"
            ref="previousButton"
            type="button"
            :class="[navArrowClass, 'summary-creative__nav--previous']"
            :aria-label="t('summary.creative.previous')"
            :title="t('summary.creative.previous')"
          >
            <Icon name="chevron-left" :size="18" aria-hidden="true" />
          </button>
          <button
            v-show="insight.rows.value.length > SLIDES_PER_VIEW"
            ref="nextButton"
            type="button"
            :class="[navArrowClass, 'summary-creative__nav--next']"
            :aria-label="t('summary.creative.next')"
            :title="t('summary.creative.next')"
          >
            <Icon name="chevron-right" :size="18" aria-hidden="true" />
          </button>
        </template>
      </div>
    </header>
    <Alert v-if="insight.state.value === 'error'" tone="danger">
      {{ t('summary.states.error') }}
      <Button size="sm" variant="outline" @click="insight.reload">{{
        t('summary.actions.retry')
      }}</Button>
    </Alert>
    <div
      v-else-if="insight.state.value === 'empty'"
      class="flex min-h-32 items-center justify-center"
    >
      {{ t('summary.states.empty') }}
    </div>
    <CreativeSliderCard
      v-else
      class="relative block min-h-0 overflow-hidden pt-sm"
      :rows="insight.rows.value"
      :slides-per-view="SLIDES_PER_VIEW"
      :prev-el="previousButton"
      :next-el="nextButton"
      :dimensions="insight.dimensions.value"
      :metrics="insight.metrics.value"
      :media-type="insight.selectedType.value ?? 'Image'"
      :state="insight.state.value"
    />
  </section>
  <Drawer
    v-if="insight.types.value.length"
    v-model="drawerOpen"
    class="summary-creative__customize-drawer"
    :title="t('summary.creative.customizeTitle')"
    :close-label="t('summary.actions.close')"
    size="md"
  >
    <ReportTableCustomizePanel
      v-model="drawerOpen"
      :columns="insight.columns.value"
      :visible-fields="visibleFields"
      :required-fields="requiredFields"
      :allow-save-view="false"
      :framed="false"
      @apply="applyCustomization"
    />
  </Drawer>
</template>
